import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Alert, AlertDescription } from "@/components/ui/alert";

import PendingRequests from "./components/PendingRequests";
import VerificationLog from "./components/VerificationLog";
import { useVerification } from "./VerificationContext";
import { apiClient } from "@/lib/apiClient";
import { getErrorMessage } from "@/lib/api";
import type { ActiveVerification, Verification } from "@cryptolect/api-client";

interface VerificationManagerProps {
  // The route mounts this at `/ciphers/:code/verifications` so the param is
  // resolved here. Falls back to the URL param at runtime so this same
  // component can be used standalone in Storybook / tests with explicit props.
  cipherCode?: string;
}

export default function VerificationManager(props: VerificationManagerProps) {
  const params = useParams<{ code?: string }>();
  const cipherCode = props.cipherCode ?? params.code ?? "";
  const queryClient = useQueryClient();
  const { setPendingCount } = useVerification();
  const [activeTab, setActiveTab] = useState<"pending" | "log">("pending");
  const [active, setActive] = useState<ActiveVerification[]>([]);
  const [streamError, setStreamError] = useState<string | null>(null);

  // Live snapshot + push updates from `/v1/ciphers/:code/verifications/live`.
  // Connection-only; the historical log uses REST (`listVerifications`) so we
  // can paginate independently of the DO snapshot which only carries pending
  // rows.
  useEffect(() => {
    if (!cipherCode) return;
    setStreamError(null);
    const stream = apiClient.streamVerifications(
      cipherCode,
      (event) => {
        if (event.type === "snapshot") {
          setActive(event.requests);
          setPendingCount(event.requests.length);
        } else if (event.type === "created") {
          setActive((prev) => {
            const next = [...prev, event.request];
            setPendingCount(next.length);
            return next;
          });
        } else if (event.type === "decided") {
          setActive((prev) => {
            const next = prev.filter((r) => r.id !== event.id);
            setPendingCount(next.length);
            return next;
          });
          queryClient.invalidateQueries({
            queryKey: ["verifications", cipherCode],
          });
        } else if (event.type === "expired") {
          const ids = new Set(event.ids);
          setActive((prev) => {
            const next = prev.filter((r) => !ids.has(r.id));
            setPendingCount(next.length);
            return next;
          });
        }
      },
      {
        onError: (err) => setStreamError(getErrorMessage(err)),
      },
    );
    return () => stream.close();
  }, [cipherCode, queryClient, setPendingCount]);

  const { data: history, isLoading: historyLoading } = useQuery({
    queryKey: ["verifications", cipherCode],
    queryFn: () => apiClient.listVerifications(cipherCode, { limit: 50 }),
    enabled: !!cipherCode && activeTab === "log",
  });

  const decideMutation = useMutation({
    mutationFn: ({
      requestId,
      decision,
    }: {
      requestId: string;
      decision: "approve" | "reject";
    }) =>
      apiClient.decideVerification(cipherCode, requestId, { decision }),
    onSuccess: (_data, variables) => {
      toast.success(
        variables.decision === "approve"
          ? "Request approved successfully"
          : "Request rejected",
      );
      queryClient.invalidateQueries({
        queryKey: ["verifications", cipherCode],
      });
    },
    onError: (err: unknown) => {
      toast.error(getErrorMessage(err));
    },
  });

  const handleApprove = (id: string) =>
    decideMutation.mutate({ requestId: id, decision: "approve" });
  const handleDeny = (id: string) =>
    decideMutation.mutate({ requestId: id, decision: "reject" });

  const verifications: Verification[] = history?.verifications ?? [];

  return (
    <div className="w-full max-w-5xl">
      <h1 className="text-3xl font-bold text-primary mb-6">
        Verification Manager
      </h1>

      <div className="h-1 w-full bg-gradient-to-r from-purple-600 via-pink-500 to-blue-600 rounded-full mb-6" />

      {streamError && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{streamError}</AlertDescription>
        </Alert>
      )}

      <Tabs
        value={activeTab}
        onValueChange={(value) => setActiveTab(value as "pending" | "log")}
        className="w-full"
      >
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger
            value="pending"
            className="data-[state=active]:bg-white"
          >
            Pending Requests ({active.length})
          </TabsTrigger>
          <TabsTrigger value="log" className="data-[state=active]:bg-white">
            Verification Log ({verifications.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pending">
          <Card className="p-6">
            <ScrollArea className="h-[clamp(400px,60vh,700px)]">
              <PendingRequests
                requests={active}
                onApprove={handleApprove}
                onDeny={handleDeny}
                cipherCode={cipherCode}
              />
            </ScrollArea>
          </Card>
        </TabsContent>

        <TabsContent value="log">
          <Card className="p-6">
            <ScrollArea className="h-[clamp(400px,60vh,700px)]">
              {historyLoading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : (
                <VerificationLog requests={verifications} />
              )}
            </ScrollArea>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
